import { useCallback, useEffect, useState } from "react";
import { Calendar, Users, Package, ClipboardList, CheckCircle2, Clock, RefreshCw, Mail, Phone, ShieldAlert, GraduationCap, UserCheck, FileVideo, Download, Ticket } from "lucide-react";
import { api, useAuth } from "@/context/AuthContext";
import { SectionTag } from "@/components/Reveal";
import { toast } from "sonner";

const TABS = [
  { id: "all", label: "All Requests", icon: ClipboardList },
  { id: "packages", label: "Package Enquiries", icon: Package },
  { id: "camps", label: "Camp Signups", icon: Users },
  { id: "members", label: "Members", icon: UserCheck },
  { id: "promos", label: "Promo Leads", icon: Ticket },
];

const isPackage = (s = "") => /package|10-pack/i.test(s);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

function StatusBadge({ status }) {
  const confirmed = status === "confirmed";
  return (
    <span className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${confirmed ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-court-volt border-court-volt/40 bg-court-volt/10"}`}>
      {confirmed ? "Confirmed" : "Pending"}
    </span>
  );
}

export default function Admin() {
  const { user, openAuthModal } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [camps, setCamps] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [members, setMembers] = useState([]);
  const [promos, setPromos] = useState([]);
  const [bcast, setBcast] = useState({ subject: "", message: "" });
  const [bcastBusy, setBcastBusy] = useState(false);

  const isAdmin = user && user.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, m, p] = await Promise.all([api.get("/bookings"), api.get("/camp/registrations"), api.get("/members"), api.get("/promo-signups")]);
      setBookings(b.data);
      setCamps(c.data);
      setMembers(m.data);
      setPromos(p.data);
      setError("");
    } catch {
      setError("Could not load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const toggleStatus = async (kind, item) => {
    const revert = kind === "bookings" ? "pending" : "reserved";
    const next = item.status === "confirmed" ? revert : "confirmed";
    try {
      await api.patch(`/${kind}/${item.id}/status`, { status: next });
      if (kind === "bookings") {
        setBookings((list) => list.map((x) => (x.id === item.id ? { ...x, status: next } : x)));
      } else {
        setCamps((list) => list.map((x) => (x.id === item.id ? { ...x, status: next } : x)));
      }
      toast.success(`Marked as ${next}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const toggleAccess = async (m) => {
    try {
      await api.patch(`/members/${m.id}/access`, { plan_access: !m.plan_access });
      setMembers((list) => list.map((x) => (x.id === m.id ? { ...x, plan_access: !m.plan_access } : x)));
      toast.success(m.plan_access ? "Vault access revoked." : `${m.name} can now open the full blueprint vault.`);
    } catch {
      toast.error("Could not update access.");
    }
  };

  const sendBroadcast = async () => {
    if (!bcast.subject.trim() || !bcast.message.trim()) {
      toast.error("Add a subject and message first.");
      return;
    }
    setBcastBusy(true);
    try {
      const { data } = await api.post("/promo-signups/broadcast", bcast);
      toast.success(`Email sent to ${data.sent} lead${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} failed)` : ""}.`);
      setBcast({ subject: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed. Try again.");
    } finally {
      setBcastBusy(false);
    }
  };

  if (user === null) {
    return <main className="pt-16 min-h-screen flex items-center justify-center"><p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading...</p></main>;
  }

  if (!isAdmin) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center px-5">
        <div data-testid="admin-locked" className="max-w-md w-full bg-court-surface/90 border border-white/10 rounded-2xl p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-court-amber/15 flex items-center justify-center mb-5">
            <ShieldAlert size={22} className="text-court-amber" />
          </div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl">Coach access only</h1>
          <p className="text-sm text-zinc-400 mt-3">This dashboard is private. Log in with Theo's admin account to view booking requests, package enquiries, and camp signups.</p>
          {!user && (
            <button data-testid="admin-login-btn" onClick={() => openAuthModal("Log in with the admin account to open the dashboard.")}
              className="btn-amber mt-6 bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg px-8 py-3 rounded-lg w-full">
              Admin Login
            </button>
          )}
        </div>
      </main>
    );
  }

  const packageBookings = bookings.filter((b) => isPackage(b.session_type));
  const pendingCount = bookings.filter((b) => b.status !== "confirmed").length + camps.filter((c) => c.status !== "confirmed").length;
  const shownBookings = tab === "packages" ? packageBookings : bookings;

  return (
    <main className="pt-16 min-h-screen">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <SectionTag index="ADMIN" label="Coach Theo's Dashboard" />
            <h1 data-testid="admin-heading" className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
              Mission <span className="text-court-amber">control.</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <a data-testid="admin-export-consents-btn" href={`${process.env.REACT_APP_BACKEND_URL}/api/consent-records/export`}
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-court-amber hover:text-court-amberhover transition-colors border border-court-amber/40 hover:border-court-amber/70 rounded-lg px-4 py-2.5">
              <Download size={13} /> Export Consents
            </a>
            <button data-testid="admin-refresh-btn" onClick={load} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-court-amber transition-colors border border-white/10 hover:border-court-amber/50 rounded-lg px-4 py-2.5">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: ClipboardList, label: "Total Requests", value: bookings.length, testid: "stat-total" },
            { icon: Clock, label: "Awaiting Reply", value: pendingCount, testid: "stat-pending" },
            { icon: Package, label: "Package Enquiries", value: packageBookings.length, testid: "stat-packages" },
            { icon: Users, label: "Camp Signups", value: camps.length, testid: "stat-camps" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-court-surface/80 border border-white/10 rounded-xl p-5">
              <s.icon size={18} className="text-court-amber" />
              <div className="font-display font-black text-4xl mt-3">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-full font-mono text-xs uppercase tracking-widest transition-colors ${tab === t.id ? "bg-court-amber text-court-void font-bold" : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {error && <p data-testid="admin-error" className="mt-8 text-sm text-red-400">{error}</p>}
        {!loading && !error && tab !== "camps" && shownBookings.length === 0 && (
          <p data-testid="admin-empty" className="mt-12 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">No requests yet — share the site and they'll land here.</p>
        )}

        {tab !== "camps" && (
          <div data-testid="admin-bookings-list" className="mt-8 space-y-4">
            {shownBookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-court-surface/80 border border-white/10 rounded-xl p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display font-bold uppercase tracking-wide text-xl">{b.name}</h3>
                      <StatusBadge status={b.status} />
                      {isPackage(b.session_type) && <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-court-amber/15 text-court-amber border border-court-amber/40">Package</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-400">
                      <span className="flex items-center gap-1.5"><Mail size={13} className="text-court-amber" />{b.email}</span>
                      <span className="flex items-center gap-1.5"><Phone size={13} className="text-court-amber" />{b.phone}</span>
                      <span className="flex items-center gap-1.5"><GraduationCap size={13} className="text-court-amber" />{b.athlete_grade}</span>
                    </div>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">{fmtDate(b.created_at)}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-xs font-mono uppercase tracking-wider">
                  <span className="bg-court-void/70 border border-white/10 rounded-lg px-3 py-1.5 text-court-amber">{b.session_type}</span>
                  <span className="bg-court-void/70 border border-white/10 rounded-lg px-3 py-1.5 text-zinc-300 flex items-center gap-1.5"><Calendar size={12} />{b.preferred_day} • {b.preferred_slot}</span>
                  {b.promo_code && <span className="bg-court-amber/15 border border-court-amber/40 rounded-lg px-3 py-1.5 text-court-amber">Promo: {b.promo_code}</span>}
                </div>
                {(b.goals || b.notes) && (
                  <div className="mt-3 text-sm text-zinc-400 space-y-1">
                    {b.goals && <p><span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Goals:</span> {b.goals}</p>}
                    {b.notes && <p><span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Notes:</span> {b.notes}</p>}
                  </div>
                )}
                {b.film_file_id && (
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <a data-testid={`booking-film-${b.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${b.film_file_id}/download`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-court-amber hover:text-court-amberhover transition-colors border border-court-amber/40 rounded-lg px-3 py-1.5">
                      <FileVideo size={13} /> Film: {b.film_filename}
                    </a>
                    {b.consent_agreed && (
                      <span data-testid={`booking-consent-${b.id}`} className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                        Media consent signed by {b.consent_signed_by}{b.consent_signed_at ? ` • ${fmtDate(b.consent_signed_at)}` : ""} • photo/audio/video: {b.consent_photo ? "Y" : "N"}/{b.consent_audio ? "Y" : "N"}/{b.consent_video ? "Y" : "N"}
                      </span>
                    )}
                  </div>
                )}
                <button data-testid={`booking-toggle-${b.id}`} onClick={() => toggleStatus("bookings", b)}
                  className={`mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-widest transition-colors ${b.status === "confirmed" ? "text-zinc-500 hover:text-court-volt" : "text-court-amber hover:text-court-amberhover"}`}>
                  <CheckCircle2 size={14} /> {b.status === "confirmed" ? "Move back to pending" : "Mark confirmed"}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "camps" && (
          <div data-testid="admin-camps-list" className="mt-8 space-y-4">
            {camps.length === 0 && <p data-testid="admin-camps-empty" className="mt-4 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">No camp signups yet.</p>}
            {camps.map((c) => (
              <div key={c.id} data-testid={`camp-row-${c.id}`} className="bg-court-surface/80 border border-white/10 rounded-xl p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display font-bold uppercase tracking-wide text-xl">{c.athlete_name}</h3>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-400">
                      <span>Parent: {c.parent_name}</span>
                      <span className="flex items-center gap-1.5"><Mail size={13} className="text-court-amber" />{c.email}</span>
                      <span className="flex items-center gap-1.5"><Phone size={13} className="text-court-amber" />{c.phone}</span>
                      <span className="flex items-center gap-1.5"><GraduationCap size={13} className="text-court-amber" />{c.athlete_grade}</span>
                    </div>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">{fmtDate(c.created_at)}</span>
                </div>
                <div className="mt-4">
                  <span className="text-xs font-mono uppercase tracking-wider bg-court-void/70 border border-white/10 rounded-lg px-3 py-1.5 text-court-amber inline-block">{c.week}</span>
                </div>
                {c.notes && <p className="mt-3 text-sm text-zinc-400"><span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Notes:</span> {c.notes}</p>}
                <button data-testid={`camp-toggle-${c.id}`} onClick={() => toggleStatus("camp/registrations", c)}
                  className={`mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-widest transition-colors ${c.status === "confirmed" ? "text-zinc-500 hover:text-court-volt" : "text-court-amber hover:text-court-amberhover"}`}>
                  <CheckCircle2 size={14} /> {c.status === "confirmed" ? "Move back to reserved" : "Mark confirmed"}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "members" && (
          <div data-testid="admin-members-list" className="mt-8 space-y-4">
            {members.length === 0 && <p data-testid="admin-members-empty" className="mt-4 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">No members yet.</p>}
            {members.map((m) => (
              <div key={m.id} data-testid={`member-row-${m.id}`} className="bg-court-surface/80 border border-white/10 rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display font-bold uppercase tracking-wide text-xl">{m.name}</h3>
                    {m.plan_access ? (
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border text-emerald-400 border-emerald-500/40 bg-emerald-500/10">Vault Unlocked</span>
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border text-court-volt border-court-volt/40 bg-court-volt/10">Awaiting Payment</span>
                    )}
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">{m.auth_provider === "google" ? "Google sign-in" : "Email"}</span>
                  </div>
                  <div className="mt-2 text-sm text-zinc-400 flex items-center gap-1.5"><Mail size={13} className="text-court-amber" />{m.email}</div>
                </div>
                {m.role !== "admin" && (
                  <button data-testid={`member-access-toggle-${m.id}`} onClick={() => toggleAccess(m)}
                    className={`font-mono text-xs uppercase tracking-widest px-4 py-2.5 rounded-lg border transition-colors ${m.plan_access ? "border-white/15 text-zinc-400 hover:text-red-400 hover:border-red-400/40" : "border-court-amber/50 text-court-amber hover:bg-court-amber hover:text-court-void"}`}>
                    {m.plan_access ? "Revoke access" : "Grant vault access"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "promos" && (
          <div data-testid="admin-promos-list" className="mt-8 space-y-4">
            <div className="bg-court-surface/90 border border-court-amber/30 rounded-xl p-6 space-y-3">
              <div className="font-mono text-xs uppercase tracking-widest text-court-amber font-bold">Email all {promos.length} lead{promos.length === 1 ? "" : "s"}</div>
              <input data-testid="promo-broadcast-subject" className="input-dark" placeholder="Subject (e.g. Saturday drop-in spots just opened)"
                value={bcast.subject} onChange={(e) => setBcast((s) => ({ ...s, subject: e.target.value }))} maxLength={120} />
              <textarea data-testid="promo-broadcast-message" className="input-dark min-h-[110px]" placeholder="Your message — short and personal works best. Their FIRST10 code is automatically mentioned at the bottom."
                value={bcast.message} onChange={(e) => setBcast((s) => ({ ...s, message: e.target.value }))} maxLength={2000} />
              <button data-testid="promo-broadcast-btn" onClick={sendBroadcast} disabled={bcastBusy || promos.length === 0}
                className="btn-amber bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg px-6 py-3 rounded-lg disabled:opacity-60">
                {bcastBusy ? "Sending..." : `Send to ${promos.length} lead${promos.length === 1 ? "" : "s"}`}
              </button>
            </div>
            {promos.length === 0 && <p data-testid="admin-promos-empty" className="mt-4 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">No promo claims yet — share the site and they'll land here.</p>}
            {promos.map((p) => (
              <div key={p.id} data-testid={`promo-row-${p.id}`} className="bg-court-surface/80 border border-white/10 rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Mail size={15} className="text-court-amber" />
                  <span className="text-sm text-zinc-200">{p.email}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-court-amber/15 text-court-amber border border-court-amber/40">{p.code}</span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">{fmtDate(p.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
